import type { TonyteActivity } from '../types';
import { getSafeTags } from './activityTags';

export type StudiZoneCategory = 'all' | 'campus' | 'parties' | 'erasmus' | 'workshops' | 'sports' | 'free';
export type StudiZoneBadge = 'Campus' | 'Erasmus' | 'Party' | 'Workshop' | 'Sports';

const buildTokenText = (activity: TonyteActivity) => [
  activity.title,
  activity.description,
  activity.locationName,
  activity.category,
  ...getSafeTags([...(activity.tags || []), ...(activity.hashtags || [])]),
].join(' ').toLowerCase();

const normalizeSearchText = (value: unknown) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

export function matchesStudiZoneSearch(activity: TonyteActivity, query: string): boolean {
  const terms = normalizeSearchText(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const searchable = normalizeSearchText([
    activity.title,
    activity.description,
    activity.locationName,
    activity.venueName,
    activity.address,
    activity.city,
    activity.category,
    ...getSafeTags([...(activity.tags || []), ...(activity.hashtags || [])]),
  ].join(' '));
  return terms.every(term => searchable.includes(term));
}

// Word boundaries are important here: "techno" must not match "technology"
// and "bar" must not match "library". Erasmus is intentionally narrower
// than a bare "international" or "exchange", both common in unrelated
// university listings.
const PARTY = /\b(?:part(?:y|ies)|techno|club(?:bing)?|nightclub|nightlife|bar|drinks?|rave|dj)\b/;
const ERASMUS = /\b(?:erasmus|esn)\b|\binternational\s+students?\b|\bexchange\s+students?\b|\bstudent\s+exchange\b/;
const CAMPUS = /\b(?:campus|university|tu\s+dresden|htw|slub|study)\b/;
const WORKSHOP = /\b(?:workshops?|study|prep|sessions?|groups?)\b/;
const SPORTS = /\b(?:sports?|football|running?|gym|yoga|hiking?)\b/;

export function matchesStudiZoneCategory(activity: TonyteActivity, category: StudiZoneCategory): boolean {
  if (category === 'all') return true;
  const tokens = buildTokenText(activity);
  if (category === 'campus') return CAMPUS.test(tokens);
  if (category === 'parties') return PARTY.test(tokens);
  if (category === 'erasmus') return ERASMUS.test(tokens);
  if (category === 'workshops') return WORKSHOP.test(tokens);
  if (category === 'sports') return SPORTS.test(tokens);
  if (category === 'free') return activity.isFree !== false || /\bfree\b/.test(tokens);
  return true;
}

export function getStudiZoneBadges(activity: TonyteActivity): StudiZoneBadge[] {
  const tokens = buildTokenText(activity);
  const badges: StudiZoneBadge[] = [];
  if (PARTY.test(tokens)) badges.push('Party');
  if (ERASMUS.test(tokens)) badges.push('Erasmus');
  if (SPORTS.test(tokens)) badges.push('Sports');
  if (WORKSHOP.test(tokens)) badges.push('Workshop');
  badges.push('Campus');
  return Array.from(new Set(badges)).slice(0, 2);
}

export function getStudiZoneTypeLabel(activity: TonyteActivity): string {
  const primary = getStudiZoneBadges(activity)[0];
  return `${primary} event`;
}

export const isFreeStudiZoneActivity = (activity: TonyteActivity) =>
  activity.isFree !== false || /\bfree\b/.test(buildTokenText(activity));
